// TES-1862: von DefectBaseDialog konsumiert (Page-Wiring Follow-up). Unit-getestet.
// fallow-ignore-file unused-file
// fallow-ignore-file unused-export
// fallow-ignore-file complexity
// TES-1862 (v2.3 §7) — Mangel-Historie-Tab. Liest die SECURITY-INVOKER-View v_defect_history (erbt die
// defects-RLS → Nicht-Gruppenmitglieder sehen 0 Einträge). Zeigt wer / wann / Feld / alt→neu mit i18n-
// Feldlabels. Reine Lese-Komponente; das Schreiben übernimmt der Audit-Trigger (Mig 337).
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";

interface DefectHistoryRow {
  id: string;
  action: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  user_id: string | null;
  created_at: string;
}

export const defectHistoryKeys = {
  list: (defectId: string) => ["defect-history", defectId] as const,
};

/** Tracked fields the audit trigger records — used for stable i18n field labels. */
const TRACKED_FIELDS = [
  "status",
  "assigned_group_id",
  "assigned_to",
  "severity",
  "verifying_qm",
  "due_date",
  "occasion_id",
] as const;

interface DefectHistoryTabProps {
  defectId: string;
  /** UI gate: only render when the caller may read the defect (RLS is the real boundary). */
  canRead?: boolean;
}

export function DefectHistoryTab({ defectId, canRead = true }: DefectHistoryTabProps) {
  const { t } = useTranslation();
  const { session } = useAuth();

  const { data: rows, isLoading } = useQuery({
    queryKey: defectHistoryKeys.list(defectId),
    enabled: canRead && !!defectId && !!session,
    staleTime: 30_000,
    queryFn: async (): Promise<DefectHistoryRow[]> => {
      const { data, error } = await supabase
        .from("v_defect_history")
        .select("id, action, old_values, new_values, user_id, created_at")
        .eq("defect_id", defectId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as DefectHistoryRow[];
    },
  });

  const fieldLabel = (field: string): string => {
    const known = (TRACKED_FIELDS as readonly string[]).includes(field);
    return known ? t(`defects.history.fields.${field}`) : field;
  };

  const renderValue = (v: unknown): string => {
    if (v === null || v === undefined || v === "") return "—";
    return String(v);
  };

  if (!canRead) return null;

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="defect-history-loading">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-3/4" />
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="defect-history-empty">
        {t("defects.history.empty")}
      </p>
    );
  }

  return (
    <ul className="space-y-3" data-testid="defect-history-list">
      {rows.map((row) => {
        const fields = Object.keys(row.new_values ?? {});
        return (
          <li
            key={row.id}
            className="rounded-md border p-2 text-sm"
            data-testid="defect-history-entry"
          >
            <div className="text-xs text-muted-foreground">
              {new Date(row.created_at).toLocaleString()} · {row.action}
            </div>
            {fields.length === 0 ? (
              <div className="text-muted-foreground">{t("defects.history.created")}</div>
            ) : (
              <ul className="mt-1 space-y-0.5">
                {fields.map((field) => (
                  <li key={field}>
                    <span className="font-medium">{fieldLabel(field)}</span>:{" "}
                    <span className="text-muted-foreground">
                      {renderValue(row.old_values?.[field])}
                    </span>{" "}
                    → <span>{renderValue(row.new_values?.[field])}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
